import React, { useState } from 'react';
import { AccountService } from '../../services/accountService';
import { getSupabaseClient } from '../../lib/supabase';
import { PasswordInput } from '@ui';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';

type AccountPasswordSetupProps = {
    onBack?: () => void;
    onPasswordSet?: (password: string) => void;
    onAccountCreated?: (email: string) => void;
    isLoading?: boolean;
    error?: string;
};

export const AccountPasswordSetup: React.FC<AccountPasswordSetupProps> = ({
    onBack,
    onPasswordSet,
    onAccountCreated,
    isLoading = false,
    error = '',
}) => {
    const [password, setPassword] = useState('');
    const [localError, setLocalError] = useState('');
    const [isSocialLoading, setIsSocialLoading] = useState(false);
    const [isCreatingAccount, setIsCreatingAccount] = useState(false);
    const [marketingConsent, setMarketingConsent] = useState(true);
    const supabase = getSupabaseClient();

    const isBusy = isLoading || isSocialLoading || isCreatingAccount;

    const handleContinue = async () => {
        if (!password || password.length < 6) {
            setLocalError('Password must be at least 6 characters long');
            return;
        }

        setLocalError('');
        setIsCreatingAccount(true);

        try {
            // Get seeker ID from storage
            const seekerId = AccountService.getSeekerIdFromStorage();
            if (!seekerId) {
                setLocalError('We could not find your account information. Please restart onboarding.');
                setIsCreatingAccount(false);
                return;
            }

            // Get email from storage
            const basicInfoRaw = localStorage.getItem('basic_info_data');
            const basicInfo = basicInfoRaw ? JSON.parse(basicInfoRaw) : {};
            const email = basicInfo?.seeker_email;

            if (!email) {
                setLocalError('Email not found. Please restart onboarding.');
                setIsCreatingAccount(false);
                return;
            }

            // Call create-seeker-account edge function
            const { data, error: functionError } = await supabase.functions.invoke('create-seeker-account', {
                body: {
                    seeker_id: seekerId,
                    password: password,
                    for_booking: false,
                    marketing_opt_in: marketingConsent,
                },
            });

            if (functionError) {
                throw new Error(functionError.message || 'Failed to create account');
            }

            if (data?.error) {
                throw new Error(data.error);
            }

            // Account created successfully, show email confirmation
            if (onAccountCreated) {
                onAccountCreated(email);
            }
        } catch (err) {
            console.error('Error creating account:', err);
            const message = err instanceof Error ? err.message : 'Failed to create account. Please try again.';
            setLocalError(message);
        } finally {
            setIsCreatingAccount(false);
        }
    };

    const handleSocialAuth = async (provider: 'google' | 'apple') => {
        setLocalError('');

        const seekerId = AccountService.getSeekerIdFromStorage();
        if (!seekerId) {
            setLocalError('We could not find your account information. Please restart onboarding.');
            return;
        }

        setIsSocialLoading(true);

        try {
            await AccountService.createSeekerAccountWithSocial(provider, seekerId);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Unable to start social sign in. Please try again.';
            setLocalError(message);
        } finally {
            setIsSocialLoading(false);
        }
    };

    const displayError = error || localError;

    return (
        <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
            <div className="relative flex flex-1 w-full flex-col bg-[#FFFCEF]">
                <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
                    <button
                        type="button"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FEF6D6]/30 bg-transparent text-xl text-[#FEF6D6] transition hover:bg-[#FEF6D6]/10 disabled:opacity-40"
                        onClick={onBack}
                        disabled={isBusy}
                        aria-label="Go back"
                    >
                        {'\u2190'}
                    </button>
                    <div className="flex flex-1 justify-center">
                        <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
                    </div>
                    <div className="w-10" aria-hidden />
                </header>
                <div className="hidden lg:block">
                    <SeekerHeader />
                </div>

                <div className="flex flex-1 flex-col items-center lg:px-[120px] lg:py-16">
                    <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center justify-center px-6 lg:px-0 w-full max-w-[480px]">
                        <div className="flex w-full flex-col gap-6">
                            <div className="text-center">
                                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#304031]/10">
                                    <svg className="h-8 w-8 text-[#304031]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                                <h1 className="font-recoleta text-[28px] leading-[1.1] text-[#304031]">Set up your account</h1>
                                <p className="mt-3 text-sm leading-relaxed text-[#304031]/70">
                                    Create a password to secure your account and finish booking your consultation.
                                </p>
                            </div>

                            <div className="rounded-2xl border border-[#304031]/10 bg-white p-5 flex flex-col gap-4">
                                <label htmlFor="account-password" className="text-sm font-semibold text-[#304031]">
                                    Password
                                </label>
                                <PasswordInput
                                    id="account-password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setLocalError('');
                                    }}
                                    className="w-full rounded-lg border border-[#304031]/30 bg-[#FFFCEF] px-3 py-2 text-center text-lg font-semibold tracking-[0.06em] text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-1 focus:ring-[#304031]"
                                    placeholder="Enter a secure password"
                                    disabled={isBusy}
                                />
                                {displayError && (
                                    <div className="rounded-2xl border border-[#B24E4E]/30 bg-[#FCEDEA] px-4 py-3 text-sm font-medium text-[#B24E4E]">
                                        {displayError}
                                    </div>
                                )}
                                <label className="flex items-start gap-3 text-sm text-[#304031]/70">
                                    <input
                                        type="checkbox"
                                        className="mt-1 h-4 w-4 rounded border-[#304031]/30 text-[#304031] focus:ring-[#304031]"
                                        checked={marketingConsent}
                                        onChange={(event) => setMarketingConsent(event.target.checked)}
                                    />
                                    <span>
                                        I consent to receive occasional marketing emails about new guides, offerings, and community resources. You can
                                        opt out anytime.
                                    </span>
                                </label>
                                <button
                                    type="button"
                                    className="w-full rounded-full bg-[#304031] px-6 py-4 text-base font-semibold text-[#FEF6D6] transition hover:bg-[#304031]/90 disabled:pointer-events-none disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]"
                                    onClick={handleContinue}
                                    disabled={isBusy || !password}
                                >
                                    {isCreatingAccount ? 'Creating account...' : isBusy ? 'Setting up...' : 'Create account'}
                                </button>
                            </div>

                            <div className="flex items-center gap-3 text-sm text-[#304031]/60">
                                <span className="h-px flex-1 bg-[#304031]/20" />
                                or continue with
                                <span className="h-px flex-1 bg-[#304031]/20" />
                            </div>

                            <button
                                type="button"
                                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#304031]/20 bg-white px-6 py-4 text-base font-semibold text-[#304031] transition hover:bg-[#304031]/5 disabled:pointer-events-none disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]"
                                onClick={() => handleSocialAuth('google')}
                                disabled={isBusy}
                            >
                                <svg className="h-5 w-5" viewBox="0 0 24 24">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                </svg>
                                {isSocialLoading ? 'Connecting...' : 'Continue with Google'}
                            </button>

                            <div className="px-4 py-3 text-sm text-[#304031]/70">
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="h-4 w-4 shrink-0 text-[#304031]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11V7a5 5 0 0110 0v4" />
                                    </svg>
                                    Your details stay private and encrypted.
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <div className="hidden lg:block">
                <Footer />
            </div>
        </div>
    );
};
